import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';

// ---------------------------------------------------------------------------
// In-memory store (survives the session, resets on hard refresh)
// ---------------------------------------------------------------------------
let _templates: any[] = [
  {
    templateId: 'TPL-001',
    templateName: 'Annual Performance Review',
    templateCode: 'APR-001',
    description: 'Yearly evaluation for all permanent staff focusing on core competencies and technical growth.',
    minRate: 1,
    maxRate: 5,
    ratingGap: 1,
    categories: [
      {
        categoryId: 'CAT-001',
        categoryName: 'Technical Skills',
        weightage: 60,
        criterias: [
          { criteriaId: 'CR-001', criteriaName: 'Clean Code', description: 'Write clean, readable, and maintainable code.' },
          { criteriaId: 'CR-002', criteriaName: 'Problem Solving', description: 'Analyse and solve complex technical issues efficiently.' }
        ]
      },
      {
        categoryId: 'CAT-002',
        categoryName: 'Soft Skills',
        weightage: 40,
        criterias: [
          { criteriaId: 'CR-003', criteriaName: 'Communication', description: 'Communicate clearly with team members and stakeholders.' }
        ]
      }
    ],
    configureEvaluation: {
      evaluationType: 'standard',
      isReportingLineFlow: false,
      isParallelExecution: false,
      previousResultsVisible: true
    },
    isPublished: true,
    isDeleted: false,
    createdBy: 'admin@mail.com',
    createdAt: '2026-01-10T00:00:00Z'
  },
  {
    templateId: 'TPL-002',
    templateName: 'Developer Performance Template',
    templateCode: 'DP-001',
    description: 'Evaluation template focused on technical skills for software developers.',
    minRate: 1,
    maxRate: 5,
    ratingGap: 1,
    categories: [
      {
        categoryId: 'CAT-001',
        categoryName: 'Technical Skills',
        weightage: 100,
        criterias: [
          { criteriaId: 'CR-001', criteriaName: 'Clean Code', description: 'Write clean, readable, and maintainable code.' },
          { criteriaId: 'CR-002', criteriaName: 'Problem Solving', description: 'Analyse and solve complex technical issues efficiently.' }
        ]
      }
    ],
    configureEvaluation: {
      evaluationType: 'standard',
      isReportingLineFlow: true,
      isParallelExecution: true,
      previousResultsVisible: false
    },
    isPublished: false,
    isDeleted: false,
    createdBy: 'admin@mail.com',
    createdAt: '2026-02-15T00:00:00Z'
  },
  {
    templateId: 'TPL-003',
    templateName: 'Team Lead 360 Feedback',
    templateCode: 'TL360-001',
    description: '360-degree feedback for team leads gathering input from peers, subordinates, and managers.',
    minRate: 1,
    maxRate: 5,
    ratingGap: 1,
    categories: [
      {
        categoryId: 'CAT-001',
        categoryName: 'Technical Skills',
        weightage: 50,
        criterias: [
          { criteriaId: 'CR-001', criteriaName: 'Clean Code', description: 'Write clean, readable, and maintainable code.' }
        ]
      },
      {
        categoryId: 'CAT-002',
        categoryName: 'Soft Skills',
        weightage: 50,
        criterias: [
          { criteriaId: 'CR-003', criteriaName: 'Communication', description: 'Communicate clearly with team members and stakeholders.' }
        ]
      }
    ],
    configureEvaluation: {
      evaluationType: '360',
      isReportingLineFlow: false,
      isParallelExecution: true,
      previousResultsVisible: false
    },
    isPublished: true,
    isDeleted: false,
    createdBy: 'admin@mail.com',
    createdAt: '2026-03-01T00:00:00Z'
  }
];

let _nextTemplateSeq = 100;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function respond(status: number, body: unknown) {
  return of(new HttpResponse({ status, body })).pipe(delay(350));
}

function ok(body: unknown)      { return respond(200, body); }
function created(body: unknown) { return respond(201, body); }

function wrap(message: string, data: unknown) {
  return { status: true, message, data };
}

// ---------------------------------------------------------------------------
// URL matchers
// ---------------------------------------------------------------------------
const RX = {
  templateCollection: /\/templates$/,
  templateCreate:     /\/template$/,
  templateItem:       /\/template\/([^/?]+)$/,
};

// ---------------------------------------------------------------------------
// Interceptor
// ---------------------------------------------------------------------------
export const mockTemplateApiInterceptor: HttpInterceptorFn = (req, next) => {
  const url    = req.url;
  const method = req.method;

  // ── GET ALL TEMPLATES ─────────────────────────────────────────────────────

  if (RX.templateCollection.test(url) && method === 'GET') {
    const data = _templates.filter(t => !t.isDeleted);
    return ok(wrap('Templates details retrieved successfully', data));
  }

  // ── CREATE TEMPLATE ───────────────────────────────────────────────────────

  if (RX.templateCreate.test(url) && method === 'POST') {
    const body = req.body as any;
    const tpl = {
      templateId:  `TPL-${++_nextTemplateSeq}`,
      templateName: body.templateName,
      templateCode: body.templateCode ?? '',
      description:  body.description ?? '',
      minRate:      body.minRate ?? 1,
      maxRate:      body.maxRate ?? 5,
      ratingGap:    body.ratingGap ?? 1,
      categories:   (body.categories ?? []).map((c: any) => ({
        ...c,
        categoryName: c.categoryName || '',
        criterias: (c.criterias ?? []).map((cr: any) => ({ ...cr, criteriaName: cr.criteriaName || '' }))
      })),
      configureEvaluation: body.configureEvaluation ?? {
        evaluationType: 'standard',
        isReportingLineFlow: false,
        isParallelExecution: false,
        previousResultsVisible: false
      },
      formModules:  body.formModules ?? null,
      isPublished:  body.isPublished ?? false,
      isDeleted:    false,
      createdBy:    'admin@mail.com',
      createdAt:    new Date().toISOString()
    };
    _templates.push(tpl);
    console.log('[MockAPI] Template created:', tpl);
    return created(wrap('Template Created Successfully', tpl));
  }

  // ── TEMPLATE ITEM ─────────────────────────────────────────────────────────

  const tplItemMatch = RX.templateItem.exec(url);
  if (tplItemMatch) {
    const id  = tplItemMatch[1];
    const idx = _templates.findIndex(t => String(t.templateId) === String(id));

    if (method === 'GET') {
      if (idx >= 0 && !_templates[idx].isDeleted) {
        return ok(wrap('Template Details retrieved Successfully', _templates[idx]));
      }
      return respond(404, { status: false, message: 'Template not found' });
    }

    if (method === 'PUT') {
      const body = req.body as any;
      if (idx >= 0) {
        _templates[idx] = {
          ..._templates[idx],
          templateName: body.templateName ?? _templates[idx].templateName,
          templateCode: body.templateCode ?? _templates[idx].templateCode,
          description:  body.description ?? _templates[idx].description,
          minRate:      body.minRate ?? _templates[idx].minRate,
          maxRate:      body.maxRate ?? _templates[idx].maxRate,
          ratingGap:    body.ratingGap ?? _templates[idx].ratingGap,
          categories:   body.categories ?? _templates[idx].categories,
          configureEvaluation: body.configureEvaluation ?? _templates[idx].configureEvaluation,
          formModules:  body.formModules ?? _templates[idx].formModules,
          isPublished:  body.isPublished ?? _templates[idx].isPublished,
          updatedBy:    'admin@mail.com',
          updatedAt:    new Date().toISOString()
        };
        console.log('[MockAPI] Template updated:', _templates[idx]);
        return ok(wrap('Template Details Updated Successfully', _templates[idx]));
      }
    }

    if (method === 'PATCH') {
      const body = req.body as any;
      if (idx >= 0) {
        if (body.isDeleted) {
          _templates[idx] = { ..._templates[idx], isDeleted: true, updatedAt: new Date().toISOString() };
          console.log('[MockAPI] Template soft-deleted:', id);
          return ok(wrap('Template Deleted Successfully.', null));
        }
        if (body.isPublished !== undefined) {
          _templates[idx] = { ..._templates[idx], isPublished: body.isPublished, updatedAt: new Date().toISOString() };
          console.log('[MockAPI] Template publish toggled:', id, body.isPublished);
          return ok(wrap('Template publish status updated successfully.', _templates[idx]));
        }
        if (body.isReportingLineFlow !== undefined) {
          _templates[idx].configureEvaluation = {
            ..._templates[idx].configureEvaluation,
            isReportingLineFlow: body.isReportingLineFlow
          };
          _templates[idx].updatedAt = new Date().toISOString();
          console.log('[MockAPI] Template ReportingLineFlow updated:', id, body.isReportingLineFlow);
          return ok(wrap('Template ReportingLineFlow changed successfully.', _templates[idx]));
        }
        if (body.isParallelExecution !== undefined) {
          _templates[idx].configureEvaluation = {
            ..._templates[idx].configureEvaluation,
            isParallelExecution: body.isParallelExecution
          };
          _templates[idx].updatedAt = new Date().toISOString();
          console.log('[MockAPI] Template ParallelExecution updated:', id, body.isParallelExecution);
          return ok(wrap('Template ParallelExecution updated successfully.', _templates[idx]));
        }
        if (body.previousResultsVisible !== undefined) {
          _templates[idx].configureEvaluation = {
            ..._templates[idx].configureEvaluation,
            previousResultsVisible: body.previousResultsVisible
          };
          _templates[idx].updatedAt = new Date().toISOString();
          console.log('[MockAPI] Template PreviousResultsVisible updated:', id, body.previousResultsVisible);
          return ok(wrap('Previous Results visibility updated successfully.', _templates[idx]));
        }
      }
      return respond(404, { status: false, message: 'Template not found' });
    }
  }

  // ── pass through anything not matched ────────────────────────────────────
  return next(req);
};
